import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, styled, Typography, Box } from "@mui/material";
import { useState, FormEvent } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (username: string, password: string) => boolean;
}

// Styled Dialog
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 1)", // Background color all black
  },
  "& .MuiDialog-paper": {
    backgroundColor: theme.colors.alpha.white[100],
    borderRadius: theme.general.borderRadius,
    padding: theme.spacing(2),
    boxShadow: "none",
    //   width: "400px",
    //   height: "300px",
    overflow: "hidden", // Prevent scroll
    paddingBottom: "0px !important",
  },
}));

const StyledDialogContent = styled(DialogContent)({
  overflow: "hidden", // Remove scroll from content
  padding: "8px 0px 0px 0px", // Adjust padding to maintain desired spacing
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.colors.alpha.black[100], // Border color
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: theme.colors.alpha.black[100], // Border color black
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.colors.alpha.black[100], // Border color black
    },
  },
  // Add styles for the label
  "& .MuiInputLabel-root": {
    color: theme.colors.alpha.black[50],
    "&.Mui-focused": {
      color: theme.colors.alpha.black[100], // Color black when active
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.colors.alpha.black[100],
  color: theme.colors.alpha.white[100],
  "&:hover": {
    backgroundColor: theme.colors.alpha.black[100],
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.colors.error.main,
  fontSize: "12px",
  textAlign: "left",
  height: "20px", // Fixed height
  visibility: "visible", // Always visible, but the text can be empty
}));

const ContentWrapper = styled(Box)({
  position: "relative",
  height: "100%",
});

const LoginDialog = ({ open, onClose, onSubmit }: LoginDialogProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!username || !password) {
      setError("Please complete all fields");
      return;
    }

    const isValid = onSubmit(username, password);

    if (!isValid) {
      setError("Invalid credentials. Please try again.");
    } else {
      setError("");
      setUsername("");
      setPassword("");
    }
  };

  const handleClose = () => {
    setError("");
    setUsername("");
    setPassword("");
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <ContentWrapper>
        <DialogTitle sx={{ p: 0, fontSize: "20px", fontWeight: "bold" }}>Login</DialogTitle>
        <form onSubmit={handleSubmit}>
          <StyledDialogContent>
            <StyledTextField
              autoFocus
              margin="dense"
              label="Username"
              type="text"
              fullWidth
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError("");
              }}
            />
            <StyledTextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
            />
            {error ? (
              <ErrorMessage
                sx={{
                  visibility: error ? "visible" : "hidden",
                  color: error ? PureLightThemeCaaS.colors.error.main : "transparent",
                }}
              >
                {/* Space when no error */}
                {error || " "}
              </ErrorMessage>
            ) : (
              <Box sx={{ height: "20px" }} /> // Space reserved when no error
            )}
          </StyledDialogContent>
          <DialogActions sx={{ pt: 1, pb: 0, pl: 0, pr: 0 }}>
            <StyledButton onClick={handleClose} type="button">
              Cancel
            </StyledButton>
            <StyledButton type="submit">Log in</StyledButton>
          </DialogActions>
        </form>
      </ContentWrapper>
    </StyledDialog>
  );
};

export default LoginDialog;
